import { useCallback, useRef } from 'react';

import { toast } from '@surface/components';
import { NOTIFICATION_WORKFLOW_KEYS } from '@surface/notifications';

import { fetchResponse } from '@/api/client';

import { EXPORT_READY_TOAST_DURATION_MS } from '../constants';

import type { RealtimeNotificationPayload } from './use-knock-notifications';

const MAX_SESSION_DEDUPE_KEYS = 100;

type SessionDedupeState = {
  keys: Set<string>;
  queue: string[];
};

/** Returns false if this key was already claimed in-session (skip duplicate delivery). */
function claimSessionDedupeKey(
  state: SessionDedupeState,
  key: string
): boolean {
  if (state.keys.has(key)) {
    return false;
  }
  state.keys.add(key);
  state.queue.push(key);
  while (state.queue.length > MAX_SESSION_DEDUPE_KEYS) {
    const removed = state.queue.shift();
    if (removed) {
      state.keys.delete(removed);
    }
  }
  return true;
}

function dedupeKeyForExportReadyItem(item: {
  id?: string;
  data?: Record<string, unknown>;
}): string {
  if (typeof item.id === 'string' && item.id.length > 0) {
    return item.id;
  }
  const data = item.data;
  const downloadUrl = data?.download_url;
  const fileName = (data?.file_name as string | undefined) ?? 'export.xlsx';
  if (typeof downloadUrl === 'string' && downloadUrl.length > 0) {
    return `${downloadUrl}::${fileName}`;
  }
  const categoryName = data?.export_category_name as string | undefined;
  return `no-url::${fileName}::${categoryName ?? ''}`;
}

let devExportReadyInvocationCount = 0;

function logExportReadyRealtimeDiagnostics(
  payload: RealtimeNotificationPayload
): void {
  if (!import.meta.env.DEV) {
    return;
  }
  devExportReadyInvocationCount += 1;
  const exportReady = payload.items.filter(
    (i) => i.source?.key === NOTIFICATION_WORKFLOW_KEYS.EXPORT_READY
  );
  const ids = exportReady
    .map((i) => i.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  const urls = exportReady
    .map((i) => (i.data as Record<string, unknown> | undefined)?.download_url)
    .filter((u): u is string => typeof u === 'string' && u.length > 0);

  console.debug('[notifications] export-ready realtime', {
    handlerInvocation: devExportReadyInvocationCount,
    timestamp: Date.now(),
    payloadItemCount: payload.items.length,
    exportReadyCount: exportReady.length,
    distinctFeedIds: new Set(ids).size,
    distinctDownloadUrls: new Set(urls).size,
    feedIdsSample: ids.slice(0, 5),
    downloadUrlPrefixes: urls.slice(0, 5).map((u) => u.slice(0, 96)),
  });
}

/**
 * Returns a stable callback to handle realtime notification payloads.
 * When the payload contains export-ready notification(s), shows a long-lived
 * success toast with a Download action per distinct item (no programmatic
 * auto-download — avoids duplicate saves when multiple tabs are open).
 * Deduped by feed id or download URL within the payload and across the session.
 */
export const useExportReadyRealtimeHandler = (): ((
  payload: RealtimeNotificationPayload
) => void) => {
  const sessionDedupeRef = useRef<SessionDedupeState>({
    keys: new Set(),
    queue: [],
  });

  return useCallback((payload: RealtimeNotificationPayload) => {
    if (!payload.items?.length) return;

    logExportReadyRealtimeDiagnostics(payload);

    const keysThisPayload = new Set<string>();

    for (const item of payload.items) {
      const sourceKey = item.source?.key ?? 'unknown';
      if (sourceKey !== NOTIFICATION_WORKFLOW_KEYS.EXPORT_READY) {
        continue;
      }

      const dedupeKey = dedupeKeyForExportReadyItem(item);
      if (keysThisPayload.has(dedupeKey)) {
        continue;
      }
      keysThisPayload.add(dedupeKey);

      if (!claimSessionDedupeKey(sessionDedupeRef.current, dedupeKey)) {
        if (import.meta.env.DEV) {
          console.debug(
            '[notifications] export-ready skipped duplicate session key',
            dedupeKey.slice(0, 80)
          );
        }
        continue;
      }

      const data = item.data as Record<string, unknown> | undefined;
      const downloadUrl = data?.download_url;
      const fileName = data?.file_name as string | undefined;
      const categoryName = data?.export_category_name as string | undefined;
      const toastTitle = categoryName
        ? `Your ${categoryName} download is ready`
        : 'Your download is ready';

      const toastId = `export-ready:${dedupeKey}`;

      const toastBase = {
        id: toastId,
        duration: EXPORT_READY_TOAST_DURATION_MS,
        dismissible: true,
      } as const;

      if (typeof downloadUrl === 'string' && downloadUrl) {
        toast.success(toastTitle, {
          ...toastBase,
          description: 'Click Download to save your file.',
          action: {
            label: 'Download',
            onClick: async () => {
              try {
                const response = await fetchResponse(downloadUrl, {
                  headers: { Accept: 'application/octet-stream' },
                });

                if (!response.ok) {
                  throw new Error(`Download failed: ${response.status}`);
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName || 'download';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Failed to download export:', error);
              }
            },
          },
        });
      } else {
        toast.success(toastTitle, {
          ...toastBase,
          description: 'Open the notifications bell to download.',
        });
      }
    }
  }, []);
};
